import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import * as Scheduled from 'features/scheduled-messages'
import { memo, useRef, useState } from 'react'
import { StackIcon } from 'shared/ui'

import * as files from '../../utils/files'
import { FormEmojiPicker } from '../Emoji/FormPicker'
import * as Files from '../Files'
import { $reply } from '../MessageActions/MessageActions'
import {
	$editMessage,
	type InnerForm,
	resetStores,
	useOnConversationChange,
	useOnEditRequest,
	useSubmit
} from './form'
import css from './MessageForm.module.scss'
import { ReplyPreview } from './ReplyPreview'
import { SubmitButton } from './Submit'
import { useRead } from './useRead'
import { insertCharacterAtCursor } from './utils/addChar'

const MemoFilesInputs = memo(FilesInputs)

export function MessageForm({
	isExpand,
	toggleExpand
}: {
	isExpand: boolean
	toggleExpand: () => void
}) {
	const ref = useRef<InnerForm | null>(null)
	useOnConversationChange(ref)
	const fileState = useStore(files.$fileState)
	const edit = useStore($editMessage)

	const isEditMode = Boolean(edit)
	const isNewMessage = !isEditMode

	const [text, setText] = useState('')
	useOnEditRequest(setText, ref)

	const markRead = useRead()

	const { handleSubmit, isLoading } = useSubmit(text)

	const reply = useStore($reply)

	return (
		<form
			className={clsx(
				css.form,
				isEditMode && css.editMode,
				isExpand && css.expanded,
				reply && isExpand && css.withHeaderExpand
			)}
			id='message-form'
			onReset={() => {
				setText('')
				resetStores()
			}}
			onSubmit={handleSubmit}
			ref={ref}
		>
			{isNewMessage && !isExpand && <Files.Preview />}
			{isNewMessage && <ReplyPreview />}

			{isEditMode && <p className={css.edit}>Edit Message</p>}

			<div className={clsx(css.grid, css.main)}>
				<textarea
					className={css.area}
					disabled={isLoading}
					name='text'
					onFocus={markRead}
					onInput={e => setText(e.currentTarget.value)}
					placeholder='Write a message...'
					value={text}
				/>

				<button
					aria-expanded={isExpand}
					aria-label='Expand textarea'
					className={`${css.expand} li-⚪`}
					onClick={toggleExpand}
					type='button'
				>
					<StackIcon icon='arrow' width={16} />
				</button>
			</div>

			{isNewMessage && isExpand && (
				<Files.ShortPreview className={css.shortFiles} onClick={toggleExpand} />
			)}
			<div className={css.actions}>
				<div className={css.subActions}>
					<MemoFilesInputs />
					{isNewMessage && <Files.GifButton />}
					<FormEmojiPicker
						onEmojiClick={emoji => {
							const area = ref.current?.text
							if (area) {
								insertCharacterAtCursor(area, emoji)
							}
						}}
					/>
				</div>
				<div className={css.submits}>
					{isNewMessage && fileState === files.FilesState.Empty && (
						<Scheduled.UI.Indicator />
					)}

					<SubmitButton isLoading={isLoading} text={text} />
				</div>
			</div>
		</form>
	)
}

function FilesInputs() {
	const isScheduledMode = useStore(Scheduled.$isScheduledShown)
	const edit = useStore($editMessage)

	if (isScheduledMode || edit) {
		return null
	}

	return [<Files.UploadFile key='⛓️‍💥' />, <Files.UploadImage key='🖼️' />]
}
